// app/frontend/pages/Billing/Cancel.tsx
import { useState } from "react";
import { Box, Container, Stack, Group, Radio, Divider } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { router } from "@inertiajs/react";
import {
  IconAlertTriangle,
  IconCheck,
  IconAlertCircle,
  IconArrowLeft,
  IconHeart,
} from "@tabler/icons-react";
import { L2KH1, L2KH2, L2KText } from "../../components/ui/Typography";
import { L2KCard } from "../../components/ui/Cards";
import { L2KButton, L2KTextarea } from "../../components/ui/Forms";
import AuthenticatedLayout from "../../components/layouts/AuthenticatedLayout";
import { SEO } from "../../components/common/SEO";

interface Reason {
  value: string;
  label: string;
}

interface CancelPageProps {
  user: {
    id: number;
    name: string;
    email: string;
    subscription_plan: string | null;
  };
  subscription: {
    current_period_end: number;
    plan: string | null;
  } | null;
  reasons: Reason[];
}

export default function Cancel({ user, subscription, reasons }: CancelPageProps) {
  const [step, setStep] = useState<"survey" | "confirm" | "success">("survey");
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [accessUntil, setAccessUntil] = useState<string | null>(null);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleContinue = () => {
    if (!selectedReason) {
      notifications.show({
        color: "red",
        title: "Required",
        message: "Please select a reason for canceling",
        icon: <IconAlertCircle />,
      });
      return;
    }
    setStep("confirm");
  };

  const handleCancel = async () => {
    setIsLoading(true);

    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const response = await fetch("/billing/cancel", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
        body: JSON.stringify({
          reason: selectedReason,
          feedback: feedback,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setAccessUntil(data.access_until);
        setStep("success");
      } else {
        notifications.show({
          color: "red",
          title: "Error",
          message: data.error || "Failed to cancel subscription",
          icon: <IconAlertCircle />,
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Something went wrong. Please try again.",
        icon: <IconAlertCircle />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async () => {
    setIsLoading(true);

    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;

    try {
      const response = await fetch("/billing/reactivate", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        credentials: "include",
      });

      if (response.ok) {
        notifications.show({
          color: "green",
          title: "Welcome back!",
          message: "Your subscription has been reactivated",
          icon: <IconCheck />,
        });
        router.visit("/billing");
      } else {
        const data = await response.json();
        notifications.show({
          color: "red",
          title: "Error",
          message: data.error || "Failed to reactivate subscription",
          icon: <IconAlertCircle />,
        });
      }
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Error",
        message: "Something went wrong. Please try again.",
        icon: <IconAlertCircle />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <SEO title="Cancel Subscription - Learntokiz" />

      <Container size="sm" py="xl">
        {step === "survey" && (
          <SurveyStep
            reasons={reasons}
            selectedReason={selectedReason}
            onReasonChange={setSelectedReason}
            feedback={feedback}
            onFeedbackChange={setFeedback}
            onContinue={handleContinue}
            subscription={subscription}
          />
        )}

        {step === "confirm" && (
          <ConfirmStep
            subscription={subscription}
            selectedReason={reasons.find((r) => r.value === selectedReason)?.label || ""}
            onBack={() => setStep("survey")}
            onConfirm={handleCancel}
            isLoading={isLoading}
          />
        )}

        {step === "success" && (
          <SuccessStep
            accessUntil={accessUntil}
            onReactivate={handleReactivate}
            isLoading={isLoading}
          />
        )}
      </Container>
    </AuthenticatedLayout>
  );
}

// Survey Step
function SurveyStep({
  reasons,
  selectedReason,
  onReasonChange,
  feedback,
  onFeedbackChange,
  onContinue,
  subscription,
}: {
  reasons: Reason[];
  selectedReason: string | null;
  onReasonChange: (value: string) => void;
  feedback: string;
  onFeedbackChange: (value: string) => void;
  onContinue: () => void;
  subscription: { current_period_end: number } | null;
}) {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap="sm" align="center" ta="center">
        <Box
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(255, 193, 7, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid rgba(255, 193, 7, 0.3)",
          }}
        >
          <IconHeart size={32} color="#FFC107" />
        </Box>
        <L2KH1>We're sorry to see you go</L2KH1>
        <L2KText variant="secondary">
          Before you cancel, please help us understand why so we can improve.
        </L2KText>
      </Stack>

      <L2KCard variant="elevated">
        <Stack gap="lg">
          <L2KH2>Why are you canceling?</L2KH2>

          <Radio.Group value={selectedReason || ""} onChange={onReasonChange}>
            <Stack gap="sm">
              {reasons.map((reason) => (
                <Box
                  key={reason.value}
                  onClick={() => onReasonChange(reason.value)}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: `1px solid ${
                      selectedReason === reason.value
                        ? "#FF9800"
                        : "rgba(255, 152, 0, 0.2)"
                    }`,
                    backgroundColor:
                      selectedReason === reason.value
                        ? "rgba(255, 152, 0, 0.1)"
                        : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Group gap="sm">
                    <Radio
                      value={reason.value}
                      label=""
                      styles={{
                        radio: {
                          backgroundColor: "transparent",
                          borderColor:
                            selectedReason === reason.value
                              ? "#FF9800"
                              : "rgba(255, 152, 0, 0.4)",
                          "&:checked": {
                            backgroundColor: "#FF9800",
                            borderColor: "#FF9800",
                          },
                        },
                      }}
                    />
                    <L2KText>{reason.label}</L2KText>
                  </Group>
                </Box>
              ))}
            </Stack>
          </Radio.Group>

          <L2KTextarea
            label="Any additional feedback? (Optional)"
            placeholder="Tell us more about your experience..."
            minRows={3}
            value={feedback}
            onChange={(e) => onFeedbackChange(e.currentTarget.value)}
          />

          {subscription && (
            <Box
              p="md"
              style={{
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                borderRadius: "8px",
                border: "1px solid rgba(59, 130, 246, 0.3)",
              }}
            >
              <L2KText size="sm" ta="center">
                If you cancel, you'll still have access until{" "}
                <strong>{formatDate(subscription.current_period_end)}</strong>
              </L2KText>
            </Box>
          )}

          <Divider color="rgba(255, 255, 255, 0.1)" />

          <Group justify="space-between">
            <L2KButton
              variant="subtle"
              leftSection={<IconArrowLeft size={18} />}
              component="a"
              href="/billing"
            >
              Keep my subscription
            </L2KButton>
            <L2KButton onClick={onContinue}>Continue</L2KButton>
          </Group>
        </Stack>
      </L2KCard>
    </Stack>
  );
}

// Confirm Step
function ConfirmStep({
  subscription,
  selectedReason,
  onBack,
  onConfirm,
  isLoading,
}: {
  subscription: { current_period_end: number } | null;
  selectedReason: string;
  onBack: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Stack gap="xl">
      <Stack gap="sm" align="center" ta="center">
        <Box
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(239, 68, 68, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid rgba(239, 68, 68, 0.3)",
          }}
        >
          <IconAlertTriangle size={32} color="#EF4444" />
        </Box>
        <L2KH1>Confirm cancellation</L2KH1>
        <L2KText variant="secondary">
          Are you sure you want to cancel your subscription?
        </L2KText>
      </Stack>

      <L2KCard variant="elevated">
        <Stack gap="lg">
          <Stack gap="sm">
            <Group justify="space-between">
              <L2KText variant="secondary">Reason</L2KText>
              <L2KText>{selectedReason}</L2KText>
            </Group>
            {subscription && (
              <Group justify="space-between">
                <L2KText variant="secondary">Access until</L2KText>
                <L2KText fw={600}>{formatDate(subscription.current_period_end)}</L2KText>
              </Group>
            )}
          </Stack>

          <Box
            p="md"
            style={{
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              borderRadius: "8px",
              border: "1px solid rgba(239, 68, 68, 0.3)",
            }}
          >
            <Stack gap="xs">
              <L2KText size="sm" fw={600}>
                What you'll lose:
              </L2KText>
              <L2KText size="sm" variant="secondary">
                - Access to all video tutorials
              </L2KText>
              <L2KText size="sm" variant="secondary">
                - Dance dictionary
              </L2KText>
              <L2KText size="sm" variant="secondary">
                - New content updates
              </L2KText>
            </Stack>
          </Box>

          <Divider color="rgba(255, 255, 255, 0.1)" />

          <Group justify="space-between">
            <L2KButton variant="subtle" onClick={onBack} disabled={isLoading}>
              Go back
            </L2KButton>
            <L2KButton
              color="red"
              onClick={onConfirm}
              loading={isLoading}
              style={{
                backgroundColor: "#EF4444",
              }}
            >
              Cancel subscription
            </L2KButton>
          </Group>
        </Stack>
      </L2KCard>
    </Stack>
  );
}

// Success Step
function SuccessStep({
  accessUntil,
  onReactivate,
  isLoading,
}: {
  accessUntil: string | null;
  onReactivate: () => void;
  isLoading: boolean;
}) {
  return (
    <Stack gap="xl">
      <Stack gap="sm" align="center" ta="center">
        <Box
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "rgba(34, 197, 94, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid rgba(34, 197, 94, 0.3)",
          }}
        >
          <IconCheck size={32} color="#22C55E" />
        </Box>
        <L2KH1>Subscription canceled</L2KH1>
        <L2KText variant="secondary">
          Your subscription has been scheduled for cancellation.
        </L2KText>
      </Stack>

      <L2KCard variant="elevated">
        <Stack gap="lg" align="center">
          {accessUntil && (
            <Box
              p="md"
              style={{
                backgroundColor: "rgba(59, 130, 246, 0.1)",
                borderRadius: "8px",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                width: "100%",
              }}
            >
              <L2KText ta="center">
                You still have full access until <strong>{accessUntil}</strong>
              </L2KText>
            </Box>
          )}

          <L2KText ta="center" variant="secondary">
            Changed your mind? You can reactivate your subscription anytime before it ends.
          </L2KText>

          <Group gap="md">
            <L2KButton variant="outline" component="a" href="/dashboard">
              Go to Dashboard
            </L2KButton>
            <L2KButton onClick={onReactivate} loading={isLoading}>
              Reactivate Subscription
            </L2KButton>
          </Group>
        </Stack>
      </L2KCard>

      <L2KText size="sm" variant="tertiary" ta="center">
        Thank you for being a member. We hope to see you again!
      </L2KText>
    </Stack>
  );
}
